/**
 * Drum kit definitions using real samples from the Dirt-Samples library.
 * Samples are fetched from GitHub (CC-licensed, tidalcycles/Dirt-Samples).
 * Falls back to basic synthesis if a fetch fails.
 */

const BASE_URL = "https://raw.githubusercontent.com/tidalcycles/Dirt-Samples/master";

// =====================
// Sample loading
// =====================

async function fetchSample(ctx, path) {
  const url = `${BASE_URL}/${path}`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Failed to fetch ${url}: ${resp.status}`);
  const arrayBuffer = await resp.arrayBuffer();
  return ctx.decodeAudioData(arrayBuffer);
}

/** Load a sample with synthesis fallback */
function samplePad(path, fallbackSynth) {
  return async (ctx) => {
    try {
      return await fetchSample(ctx, path);
    } catch (e) {
      console.warn(`Sample fetch failed for ${path}, using synthesis fallback`, e);
      return fallbackSynth(ctx);
    }
  };
}

// =====================
// Synthesis fallbacks (used if CDN is unreachable)
// =====================

function createNoiseBuffer(ctx, duration) {
  const sampleRate = ctx.sampleRate;
  const length = sampleRate * duration;
  const buffer = ctx.createBuffer(1, length, sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  return buffer;
}

function renderOffline(duration, sampleRate, setupFn) {
  const length = Math.ceil(sampleRate * duration);
  const offline = new OfflineAudioContext(1, length, sampleRate);
  setupFn(offline);
  return offline.startRendering();
}

function synthKick(ctx) {
  return renderOffline(0.5, ctx.sampleRate, (off) => {
    const o = off.createOscillator();
    const g = off.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(150, 0);
    o.frequency.exponentialRampToValueAtTime(30, 0.12);
    g.gain.setValueAtTime(1, 0);
    g.gain.exponentialRampToValueAtTime(0.001, 0.5);
    o.connect(g).connect(off.destination);
    o.start(0); o.stop(0.5);
  });
}

function synthSnare(ctx) {
  return renderOffline(0.3, ctx.sampleRate, (off) => {
    const nb = createNoiseBuffer(off, 0.3);
    const n = off.createBufferSource(); n.buffer = nb;
    const f = off.createBiquadFilter(); f.type = "highpass"; f.frequency.value = 1000;
    const ng = off.createGain(); ng.gain.setValueAtTime(0.7, 0); ng.gain.exponentialRampToValueAtTime(0.001, 0.3);
    n.connect(f).connect(ng).connect(off.destination); n.start(0);
    const o = off.createOscillator(); const og = off.createGain();
    o.type = "triangle"; o.frequency.setValueAtTime(200, 0); o.frequency.exponentialRampToValueAtTime(80, 0.07);
    og.gain.setValueAtTime(0.6, 0); og.gain.exponentialRampToValueAtTime(0.001, 0.15);
    o.connect(og).connect(off.destination); o.start(0); o.stop(0.3);
  });
}

function synthHHClosed(ctx) {
  return renderOffline(0.08, ctx.sampleRate, (off) => {
    const nb = createNoiseBuffer(off, 0.08);
    const n = off.createBufferSource(); n.buffer = nb;
    const f = off.createBiquadFilter(); f.type = "highpass"; f.frequency.value = 7000;
    const g = off.createGain(); g.gain.setValueAtTime(0.5, 0); g.gain.exponentialRampToValueAtTime(0.001, 0.08);
    n.connect(f).connect(g).connect(off.destination); n.start(0);
  });
}

function synthHHOpen(ctx) {
  return renderOffline(0.4, ctx.sampleRate, (off) => {
    const nb = createNoiseBuffer(off, 0.4);
    const n = off.createBufferSource(); n.buffer = nb;
    const f = off.createBiquadFilter(); f.type = "highpass"; f.frequency.value = 7000;
    const g = off.createGain(); g.gain.setValueAtTime(0.5, 0); g.gain.exponentialRampToValueAtTime(0.001, 0.4);
    n.connect(f).connect(g).connect(off.destination); n.start(0);
  });
}

function synthClap(ctx) {
  return renderOffline(0.3, ctx.sampleRate, (off) => {
    const nb = createNoiseBuffer(off, 0.3);
    const n = off.createBufferSource(); n.buffer = nb;
    const f = off.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 2500; f.Q.value = 3;
    const g = off.createGain(); g.gain.setValueAtTime(0, 0);
    for (let i = 0; i < 3; i++) { const t = i * 0.015; g.gain.setValueAtTime(0.8, t); g.gain.exponentialRampToValueAtTime(0.1, t + 0.01); }
    g.gain.setValueAtTime(0.8, 0.05); g.gain.exponentialRampToValueAtTime(0.001, 0.3);
    n.connect(f).connect(g).connect(off.destination); n.start(0);
  });
}

function synthTom(ctx, pitch = 100) {
  return renderOffline(0.4, ctx.sampleRate, (off) => {
    const o = off.createOscillator(); const g = off.createGain();
    o.type = "sine"; o.frequency.setValueAtTime(pitch * 1.5, 0); o.frequency.exponentialRampToValueAtTime(pitch, 0.08);
    g.gain.setValueAtTime(0.8, 0); g.gain.exponentialRampToValueAtTime(0.001, 0.4);
    o.connect(g).connect(off.destination); o.start(0); o.stop(0.4);
  });
}

function synthRim(ctx) {
  return renderOffline(0.05, ctx.sampleRate, (off) => {
    const o = off.createOscillator(); const g = off.createGain();
    o.type = "square"; o.frequency.setValueAtTime(800, 0);
    g.gain.setValueAtTime(0.6, 0); g.gain.exponentialRampToValueAtTime(0.001, 0.05);
    o.connect(g).connect(off.destination); o.start(0); o.stop(0.05);
  });
}

function synthCowbell(ctx) {
  return renderOffline(0.3, ctx.sampleRate, (off) => {
    const o1 = off.createOscillator(); const o2 = off.createOscillator(); const g = off.createGain();
    o1.type = "square"; o1.frequency.value = 560; o2.type = "square"; o2.frequency.value = 845;
    g.gain.setValueAtTime(0.5, 0); g.gain.exponentialRampToValueAtTime(0.001, 0.3);
    const f = off.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 800; f.Q.value = 5;
    o1.connect(g); o2.connect(g); g.connect(f).connect(off.destination);
    o1.start(0); o2.start(0); o1.stop(0.3); o2.stop(0.3);
  });
}

function synthCrash(ctx) {
  return renderOffline(1.2, ctx.sampleRate, (off) => {
    const nb = createNoiseBuffer(off, 1.2);
    const n = off.createBufferSource(); n.buffer = nb;
    const f = off.createBiquadFilter(); f.type = "highpass"; f.frequency.value = 5000;
    const g = off.createGain(); g.gain.setValueAtTime(0.6, 0); g.gain.exponentialRampToValueAtTime(0.001, 1.2);
    n.connect(f).connect(g).connect(off.destination); n.start(0);
  });
}

function synthRide(ctx) {
  return renderOffline(0.8, ctx.sampleRate, (off) => {
    const nb = createNoiseBuffer(off, 0.8);
    const n = off.createBufferSource(); n.buffer = nb;
    const f = off.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 8000; f.Q.value = 2;
    const g = off.createGain(); g.gain.setValueAtTime(0.4, 0); g.gain.exponentialRampToValueAtTime(0.001, 0.8);
    n.connect(f).connect(g).connect(off.destination); n.start(0);
  });
}

function synthShaker(ctx) {
  return renderOffline(0.12, ctx.sampleRate, (off) => {
    const nb = createNoiseBuffer(off, 0.12);
    const n = off.createBufferSource(); n.buffer = nb;
    const f = off.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 9000; f.Q.value = 1;
    const g = off.createGain(); g.gain.setValueAtTime(0.3, 0); g.gain.exponentialRampToValueAtTime(0.001, 0.12);
    n.connect(f).connect(g).connect(off.destination); n.start(0);
  });
}

function synthPerc(ctx) {
  return renderOffline(0.15, ctx.sampleRate, (off) => {
    const o = off.createOscillator(); const g = off.createGain();
    o.type = "sine"; o.frequency.setValueAtTime(400, 0); o.frequency.exponentialRampToValueAtTime(200, 0.15);
    g.gain.setValueAtTime(0.5, 0); g.gain.exponentialRampToValueAtTime(0.001, 0.15);
    o.connect(g).connect(off.destination); o.start(0); o.stop(0.15);
  });
}

function synthFX(ctx) {
  return renderOffline(0.6, ctx.sampleRate, (off) => {
    const o = off.createOscillator(); const g = off.createGain();
    o.type = "sawtooth"; o.frequency.setValueAtTime(800, 0); o.frequency.exponentialRampToValueAtTime(100, 0.6);
    g.gain.setValueAtTime(0.3, 0); g.gain.exponentialRampToValueAtTime(0.001, 0.6);
    const f = off.createBiquadFilter(); f.type = "lowpass"; f.frequency.setValueAtTime(4000, 0); f.frequency.exponentialRampToValueAtTime(200, 0.6);
    o.connect(f).connect(g).connect(off.destination); o.start(0); o.stop(0.6);
  });
}

// =====================
// Kit 1: Classic 808 — real Roland TR-808 samples
// =====================

const CLASSIC_808 = {
  id: "classic-808",
  name: "Classic 808",
  pads: [
    { name: "Kick",      color: "#e53935", synthesize: samplePad("808bd/BD0075.WAV", synthKick) },
    { name: "Snare",     color: "#fb8c00", synthesize: samplePad("808sd/SD0050.WAV", synthSnare) },
    { name: "Closed HH", color: "#fdd835", synthesize: samplePad("808hc/HC25.WAV", synthHHClosed) },
    { name: "Open HH",   color: "#43a047", synthesize: samplePad("808oh/OH25.WAV", synthHHOpen) },
    { name: "Clap",      color: "#1e88e5", synthesize: samplePad("808/CP.WAV", synthClap) },
    { name: "Tom Low",   color: "#8e24aa", synthesize: samplePad("808lt/LT50.WAV", (ctx) => synthTom(ctx, 80)) },
    { name: "Tom Mid",   color: "#00acc1", synthesize: samplePad("808mt/MT50.WAV", (ctx) => synthTom(ctx, 120)) },
    { name: "Tom High",  color: "#f4511e", synthesize: samplePad("808ht/HT50.WAV", (ctx) => synthTom(ctx, 180)) },
    { name: "Rim",       color: "#6d4c41", synthesize: samplePad("808/RS.WAV", synthRim) },
    { name: "Cowbell",   color: "#546e7a", synthesize: samplePad("808/CB.WAV", synthCowbell) },
    { name: "Cymbal",    color: "#d81b60", synthesize: samplePad("808cy/CY0075.WAV", synthCrash) },
    { name: "Conga Hi",  color: "#3949ab", synthesize: samplePad("808lc/LC50.WAV", synthRide) },
    { name: "Conga Mid", color: "#00897b", synthesize: samplePad("808mc/MC50.WAV", synthShaker) },
    { name: "Clave",     color: "#c0ca33", synthesize: samplePad("808/CL.WAV", synthPerc) },
    { name: "Maraca",    color: "#7cb342", synthesize: samplePad("808/MA.WAV", synthPerc) },
    { name: "Claves",    color: "#5e35b1", synthesize: samplePad("808/CH.WAV", synthFX) },
  ],
};

// =====================
// Kit 2: Acoustic — Gretsch drum kit (real recordings)
// =====================

const ACOUSTIC_KIT = {
  id: "acoustic",
  name: "Acoustic",
  pads: [
    { name: "Kick",        color: "#8d6e63", synthesize: samplePad("gretsch/013_kick.wav", synthKick) },
    { name: "Snare",       color: "#a1887f", synthesize: samplePad("gretsch/020_snare.wav", synthSnare) },
    { name: "Snare Hard",  color: "#bcaaa4", synthesize: samplePad("gretsch/022_snarehard.wav", synthSnare) },
    { name: "Snare Ghost", color: "#d7ccc8", synthesize: samplePad("gretsch/021_snareghost.wav", synthSnare) },
    { name: "Closed HH",   color: "#795548", synthesize: samplePad("gretsch/004_closedhat.wav", synthHHClosed) },
    { name: "Open HH",     color: "#6d4c41", synthesize: samplePad("gretsch/017_openhat.wav", synthHHOpen) },
    { name: "Foot HH",     color: "#5d4037", synthesize: samplePad("gretsch/010_foothat.wav", synthHHClosed) },
    { name: "Hi Tom",      color: "#4e342e", synthesize: samplePad("gretsch/012_hitom.wav", (ctx) => synthTom(ctx, 180)) },
    { name: "Lo Tom",      color: "#efebe9", synthesize: samplePad("gretsch/015_lotom.wav", (ctx) => synthTom(ctx, 80)) },
    { name: "Brush Snare", color: "#ff8a65", synthesize: samplePad("gretsch/002_brushsnare.wav", synthSnare) },
    { name: "Ride",        color: "#ffab91", synthesize: samplePad("gretsch/019_ridecymbal.wav", synthRide) },
    { name: "Ride Bell",   color: "#bf360c", synthesize: samplePad("gretsch/018_ridebell.wav", synthRide) },
    { name: "Cymbal Grab", color: "#dd2c00", synthesize: samplePad("gretsch/007_cymbalgrab.wav", synthCrash) },
    { name: "Cowbell",     color: "#ff6e40", synthesize: samplePad("gretsch/006_cowbell.wav", synthCowbell) },
    { name: "Flam",        color: "#ff3d00", synthesize: samplePad("gretsch/009_flam.wav", synthSnare) },
    { name: "Kick+Snare",  color: "#e64a19", synthesize: samplePad("gretsch/014_kicksnare.wav", synthKick) },
  ],
};

// =====================
// Kit 3: Electronic — tech/electronic samples
// =====================

const ELECTRONIC_KIT = {
  id: "electronic",
  name: "Electronic",
  pads: [
    { name: "Kick 1",     color: "#00e5ff", synthesize: samplePad("tech/tn1kick1.wav", synthKick) },
    { name: "Kick 2",     color: "#18ffff", synthesize: samplePad("tech/tn1kick2.wav", synthKick) },
    { name: "Snare 1",    color: "#84ffff", synthesize: samplePad("tech/tn1snare1.wav", synthSnare) },
    { name: "Snare 2",    color: "#00bcd4", synthesize: samplePad("tech/tn1snare2.wav", synthSnare) },
    { name: "Closed HH",  color: "#e040fb", synthesize: samplePad("tech/tn1closedhh.wav", synthHHClosed) },
    { name: "Open HH",    color: "#ea80fc", synthesize: samplePad("tech/tn1openhh.wav", synthHHOpen) },
    { name: "Crash",      color: "#d500f9", synthesize: samplePad("tech/tn1crash.wav", synthCrash) },
    { name: "Ride",       color: "#651fff", synthesize: samplePad("tech/tn1ride.wav", synthRide) },
    { name: "Hit 1",      color: "#aa00ff", synthesize: samplePad("tech/tn1hit1.wav", synthPerc) },
    { name: "Hit 2",      color: "#6200ea", synthesize: samplePad("tech/tn1hit2.wav", synthPerc) },
    { name: "Hit 3",      color: "#9c27b0", synthesize: samplePad("tech/tn1hit3.wav", synthPerc) },
    { name: "Perc 1",     color: "#ce93d8", synthesize: samplePad("tech/tn1perc1.wav", synthPerc) },
    { name: "Perc 2",     color: "#ab47bc", synthesize: samplePad("tech/tn1perc2.wav", synthPerc) },
    { name: "Club Kick 1", color: "#69f0ae", synthesize: samplePad("clubkick/1.wav", synthKick) },
    { name: "Club Kick 2", color: "#00e676", synthesize: samplePad("clubkick/3.wav", synthKick) },
    { name: "Hard Kick",  color: "#00c853", synthesize: samplePad("hardkick/VEC1 BD Distortion 06.wav", synthKick) },
  ],
};

// =====================
// Kit 4: Hip Hop — mixed 808/909 kicks + snares + claps
// =====================

const HIPHOP_KIT = {
  id: "hiphop",
  name: "Hip Hop",
  pads: [
    { name: "808 Kick",   color: "#7c4dff", synthesize: samplePad("808bd/BD0050.WAV", synthKick) },
    { name: "Hard Kick",  color: "#b388ff", synthesize: samplePad("future/000_808KICK4.wav", synthKick) },
    { name: "Snare",      color: "#e040fb", synthesize: samplePad("808sd/SD0075.WAV", synthSnare) },
    { name: "Clap",       color: "#ea80fc", synthesize: samplePad("realclaps/1.wav", synthClap) },
    { name: "Clap 2",     color: "#d500f9", synthesize: samplePad("realclaps/3.wav", synthClap) },
    { name: "Closed HH",  color: "#651fff", synthesize: samplePad("808hc/HC50.WAV", synthHHClosed) },
    { name: "Open HH",    color: "#aa00ff", synthesize: samplePad("808oh/OH50.WAV", synthHHOpen) },
    { name: "Cymbal",     color: "#6200ea", synthesize: samplePad("808cy/CY0050.WAV", synthCrash) },
    { name: "Tom Low",    color: "#9c27b0", synthesize: samplePad("808lt/LT75.WAV", (ctx) => synthTom(ctx, 80)) },
    { name: "Tom High",   color: "#ce93d8", synthesize: samplePad("808ht/HT75.WAV", (ctx) => synthTom(ctx, 180)) },
    { name: "Rim",        color: "#ab47bc", synthesize: samplePad("808/RS.WAV", synthRim) },
    { name: "Cowbell",    color: "#7b1fa2", synthesize: samplePad("808/CB.WAV", synthCowbell) },
    { name: "Conga 1",    color: "#69f0ae", synthesize: samplePad("future/015_lockett_conga_mute2.wav", synthPerc) },
    { name: "Conga 2",    color: "#00e676", synthesize: samplePad("future/016_lockett_conga_open1.wav", synthPerc) },
    { name: "Shake",      color: "#76ff03", synthesize: samplePad("future/013_Shake2.wav", synthShaker) },
    { name: "808 Kick 2", color: "#c6ff00", synthesize: samplePad("future/001_808KICK9.wav", synthKick) },
  ],
};

// =====================
// Kit 5: House — house kit samples
// =====================

const HOUSE_KIT = {
  id: "house",
  name: "House",
  pads: [
    { name: "Kick",       color: "#ff6d00", synthesize: samplePad("house/000_BD.wav", synthKick) },
    { name: "Snare",      color: "#ff9100", synthesize: samplePad("house/007_SN.wav", synthSnare) },
    { name: "Closed HH",  color: "#ffab00", synthesize: samplePad("house/003_HH.wav", synthHHClosed) },
    { name: "Open HH",    color: "#ffd600", synthesize: samplePad("house/004_OH.wav", synthHHOpen) },
    { name: "Clap",       color: "#aeea00", synthesize: samplePad("cp/HANDCLP0.wav", synthClap) },
    { name: "Clap 2",     color: "#64dd17", synthesize: samplePad("cp/HANDCLPA.wav", synthClap) },
    { name: "Perc 1",     color: "#00c853", synthesize: samplePad("house/005_P1.wav", synthPerc) },
    { name: "Perc 2",     color: "#00bfa5", synthesize: samplePad("house/006_P2.wav", synthPerc) },
    { name: "Cowbell",    color: "#00b8d4", synthesize: samplePad("house/001_CB.wav", synthCowbell) },
    { name: "FX",         color: "#0091ea", synthesize: samplePad("house/002_FX.wav", synthFX) },
    { name: "Club Kick 1", color: "#2962ff", synthesize: samplePad("clubkick/2.wav", synthKick) },
    { name: "Club Kick 2", color: "#304ffe", synthesize: samplePad("clubkick/4.wav", synthKick) },
    { name: "Stomp 1",    color: "#6200ea", synthesize: samplePad("stomp/002_1.wav", synthKick) },
    { name: "Stomp 2",    color: "#aa00ff", synthesize: samplePad("stomp/003_2.wav", synthKick) },
    { name: "Pop Kick",   color: "#d500f9", synthesize: samplePad("popkick/002_2.wav", synthKick) },
    { name: "Hard Kick",  color: "#c51162", synthesize: samplePad("hardkick/VEC1 BD Distortion 37.wav", synthKick) },
  ],
};

// =====================
// Kit 6: Jazz — jazz kit samples
// =====================

const JAZZ_KIT = {
  id: "jazz",
  name: "Jazz",
  pads: [
    { name: "Kick",       color: "#4e342e", synthesize: samplePad("jazz/000_BD.wav", synthKick) },
    { name: "Snare",      color: "#5d4037", synthesize: samplePad("jazz/007_SN.wav", synthSnare) },
    { name: "Closed HH",  color: "#6d4c41", synthesize: samplePad("jazz/003_HH.wav", synthHHClosed) },
    { name: "Open HH",    color: "#795548", synthesize: samplePad("jazz/004_OH.wav", synthHHOpen) },
    { name: "Perc 1",     color: "#8d6e63", synthesize: samplePad("jazz/005_P1.wav", synthPerc) },
    { name: "Perc 2",     color: "#a1887f", synthesize: samplePad("jazz/006_P2.wav", synthPerc) },
    { name: "Cowbell",    color: "#bcaaa4", synthesize: samplePad("jazz/001_CB.wav", synthCowbell) },
    { name: "FX",         color: "#d7ccc8", synthesize: samplePad("jazz/002_FX.wav", synthFX) },
    { name: "Brush Snare", color: "#efebe9", synthesize: samplePad("gretsch/002_brushsnare.wav", synthSnare) },
    { name: "Brush Ghost", color: "#ffcc80", synthesize: samplePad("gretsch/003_brushsnareghost.wav", synthSnare) },
    { name: "Brush Hi Tom", color: "#ffe0b2", synthesize: samplePad("gretsch/000_brushhitom.wav", (ctx) => synthTom(ctx, 180)) },
    { name: "Brush Lo Tom", color: "#ffab91", synthesize: samplePad("gretsch/001_brushlotom.wav", (ctx) => synthTom(ctx, 80)) },
    { name: "Ride",        color: "#ff8a65", synthesize: samplePad("gretsch/019_ridecymbal.wav", synthRide) },
    { name: "Ride Bell",   color: "#ff7043", synthesize: samplePad("gretsch/018_ridebell.wav", synthRide) },
    { name: "Cymbal Rub",  color: "#f4511e", synthesize: samplePad("gretsch/008_cymbalrub.wav", synthCrash) },
    { name: "Foot HH",     color: "#bf360c", synthesize: samplePad("gretsch/010_foothat.wav", synthHHClosed) },
  ],
};

// =====================
// Kit 7: DrumTraks — Sequential Circuits DrumTraks
// =====================

const DRUMTRAKS_KIT = {
  id: "drumtraks",
  name: "DrumTraks",
  pads: [
    { name: "Kick",       color: "#f44336", synthesize: samplePad("drumtraks/006_DT Kick.wav", synthKick) },
    { name: "Snare",      color: "#e91e63", synthesize: samplePad("drumtraks/009_DT Snare.wav", synthSnare) },
    { name: "Claps",      color: "#9c27b0", synthesize: samplePad("drumtraks/001_DT Claps.wav", synthClap) },
    { name: "Closed HH",  color: "#673ab7", synthesize: samplePad("drumtraks/004_DT Hat Closed.wav", synthHHClosed) },
    { name: "Open HH",    color: "#3f51b5", synthesize: samplePad("drumtraks/005_DT Hat Open.wav", synthHHOpen) },
    { name: "Crash",      color: "#2196f3", synthesize: samplePad("drumtraks/003_DT Crash.wav", synthCrash) },
    { name: "Ride",       color: "#03a9f4", synthesize: samplePad("drumtraks/007_DT Ride.wav", synthRide) },
    { name: "Rimshot",    color: "#00bcd4", synthesize: samplePad("drumtraks/008_DT Rimshot.wav", synthRim) },
    { name: "Tom 1",      color: "#009688", synthesize: samplePad("drumtraks/011_DT Tom1.wav", (ctx) => synthTom(ctx, 120)) },
    { name: "Tom 2",      color: "#4caf50", synthesize: samplePad("drumtraks/012_DT Tom2.wav", (ctx) => synthTom(ctx, 80)) },
    { name: "Cowbell",    color: "#8bc34a", synthesize: samplePad("drumtraks/002_DT Cowbell.wav", synthCowbell) },
    { name: "Cabasa",     color: "#cddc39", synthesize: samplePad("drumtraks/000_DT Cabasa.wav", synthShaker) },
    { name: "Tambourine", color: "#ffeb3b", synthesize: samplePad("drumtraks/010_DT Tambourine.wav", synthShaker) },
    { name: "808 Clap",   color: "#ffc107", synthesize: samplePad("808/CP.WAV", synthClap) },
    { name: "808 Rim",    color: "#ff9800", synthesize: samplePad("808/RS.WAV", synthRim) },
    { name: "808 Cowbell", color: "#ff5722", synthesize: samplePad("808/CB.WAV", synthCowbell) },
  ],
};

// =====================
// Registry
// =====================

export const KITS = [CLASSIC_808, ACOUSTIC_KIT, ELECTRONIC_KIT, HIPHOP_KIT, HOUSE_KIT, JAZZ_KIT, DRUMTRAKS_KIT];

export const DEFAULT_KIT = CLASSIC_808;

export function getKitById(id) {
  return KITS.find((k) => k.id === id) || DEFAULT_KIT;
}

export async function generateKitBuffers(audioCtx, kit) {
  const buffers = await Promise.all(
    kit.pads.map((pad) => pad.synthesize(audioCtx))
  );
  return buffers;
}
